import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useIsStandard } from '@contexts/operator/PlanContext';

import { operatorApiFetch } from '@services/operator/apiService';
import { merchantService } from '@services/operator/merchantService';
import { inventoryTypeService } from '@services/operator/inventoryTypeService';
import { shelfTypeService } from '@services/operator/shelfTypeService';
import { shippingTypeService } from '@services/operator/shippingTypeService';
import { merchantShippingTypesService } from '@services/operator/merchantShippingTypesService';
import { salesChannelService } from '@services/operator/salesChannelService';
import {
  getTabId,
  getStoredMerchantSelection,
  setStoredMerchantSelection,
  clearStoredMerchantSelection,
} from '@/utils/tabSession';

interface MerchantSummary {
  id: string;
  name: string;
  order_item_columns?: string[];
  available_order_item_columns?: string[];
  [key: string]: unknown;
}

interface InventoryTypeRecord {
  id: string;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

interface ShelfTypeRecord {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface ShippingTypeRecord {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface SalesChannelRecord {
  id: string;
  name: string;
  merchant_id?: string;
  [key: string]: unknown;
}

interface CurrencyRecord {
  code: string;
  symbol: string;
  name_key: string;
  [key: string]: unknown;
}

interface MerchantShippingTypeRow {
  id: string;
  name: string;
  code: string;
  source?: string;
  supports_cod?: boolean;
  supports_delivery_with_pickup?: boolean;
  price?: number | string | null;
  return_shipping_fee?: number | string | null;
  shipping_rate_card_id?: string | null;
  [key: string]: unknown;
}

interface OperatorDataContextValue {
  inventoryTypes: InventoryTypeRecord[];
  merchants: MerchantSummary[];
  merchantId: string | null;
  merchant: MerchantSummary | null;
  currentMerchant: MerchantSummary | null;
  loading: boolean;
  error: string | null;
  refreshOperatorData: () => Promise<void>;
  setCurrentMerchant: (selectedMerchant: MerchantSummary | null) => Promise<void> | void;
  resetMerchant: () => void;
  salesChannels: SalesChannelRecord[];
  setSalesChannels: React.Dispatch<React.SetStateAction<SalesChannelRecord[]>>;
  shelfTypes: ShelfTypeRecord[];
  setShelfTypes: React.Dispatch<React.SetStateAction<ShelfTypeRecord[]>>;
  shippingTypes: ShippingTypeRecord[];
  setShippingTypes: React.Dispatch<React.SetStateAction<ShippingTypeRecord[]>>;
  currencies: CurrencyRecord[];
  /** Organization 體積單位（cbm / cuft），供材積顯示用；登入後載入一次 */
  volumeUnit: string | null;
  merchantInventoryTypes: any[];
  setMerchantInventoryTypes: React.Dispatch<React.SetStateAction<any[]>>;
  refreshMerchantInventoryTypes: () => Promise<any[]>;
  merchantShippingTypes: MerchantShippingTypeRow[];
  setMerchantShippingTypes: React.Dispatch<React.SetStateAction<MerchantShippingTypeRow[]>>;
  refreshMerchantShippingTypes: () => Promise<MerchantShippingTypeRow[]>;
  merchantSalesChannels: SalesChannelRecord[];
  setMerchantSalesChannels: React.Dispatch<React.SetStateAction<SalesChannelRecord[]>>;
  refreshMerchantSalesChannels: () => Promise<SalesChannelRecord[]>;
  orderItemColumns: string[];
  availableOrderItemColumns: string[];
  updateOrderItemColumns: (columns: string[]) => void;
  // 既有 callers（如 PickingListPresets/List）destructure 此處未明確列出的欄位
  // (e.g. warehouses)；以 index signature 維持 .jsx 時期 callers 的鬆綁存取行為。
  [key: string]: any;
}

const OperatorDataContext = createContext<OperatorDataContextValue | undefined>(undefined);

// 通用分頁取得所有資料的 helper（使用 since_id 分頁）
// 用於 Select 下拉選單等需要完整資料的場景
const fetchAllPages = async <T = unknown>(endpoint: string, pageSize = 200): Promise<T[]> => {
  let allData: T[] = [];
  let sinceId: string | null = null;
  let hasMore = true;
  let iteration = 0;
  const maxIterations = 50;

  while (hasMore && iteration < maxIterations) {
    iteration += 1;
    const url = sinceId
      ? `${endpoint}?page_size=${pageSize}&since_id=${sinceId}`
      : `${endpoint}?page_size=${pageSize}`;

    const response = await operatorApiFetch(url);
    const json = await response.json();

    const data: T[] = json.data || [];
    const newSinceId: string | null = json.since_id ?? null;

    // 檢查 since_id 是否有變化，避免無限迴圈
    // （後端 API 必須使用 render_data_list + Relation，否則 since_id 不會正確更新）
    if (sinceId !== null && newSinceId === sinceId) {
      break;
    }

    allData = [...allData, ...data];
    hasMore = json.has_more === true;
    sinceId = newSinceId;

    // 如果沒有資料，停止
    if (data.length === 0) {
      break;
    }
  }

  return allData;
};

interface OperatorDataProviderProps {
  children: ReactNode;
}

export const OperatorDataProvider: React.FC<OperatorDataProviderProps> = ({ children }) => {
  const { isAuthenticated, loading: authLoading, operator: currentOperator } = useOperatorAuth() as {
    isAuthenticated: boolean;
    loading: boolean;
    operator: { id?: string } | null;
  };
  const isStandard = useIsStandard();

  // 狀態管理
  const [inventoryTypes, setInventoryTypes] = useState<InventoryTypeRecord[]>([]);
  const [merchants, setMerchants] = useState<MerchantSummary[]>([]);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [merchant, setMerchant] = useState<MerchantSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [salesChannels, setSalesChannels] = useState<SalesChannelRecord[]>([]);
  const [shelfTypes, setShelfTypes] = useState<ShelfTypeRecord[]>([]);
  const [shippingTypes, setShippingTypes] = useState<ShippingTypeRecord[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyRecord[]>([]);
  const [volumeUnit, setVolumeUnit] = useState<string | null>(null);
  const [merchantInventoryTypes, setMerchantInventoryTypes] = useState<any[]>([]);
  const [merchantShippingTypes, setMerchantShippingTypes] = useState<MerchantShippingTypeRow[]>([]);
  const [merchantSalesChannels, setMerchantSalesChannels] = useState<SalesChannelRecord[]>([]);
  const [orderItemColumns, setOrderItemColumns] = useState<string[]>([]); // Enabled columns
  const [availableOrderItemColumns, setAvailableOrderItemColumns] = useState<string[]>([]);


  // 獲取 inventory types
  const fetchInventoryTypes = async (): Promise<InventoryTypeRecord[]> => {
    try {
      const result = await inventoryTypeService.listAll();
      return result.data || [];
    } catch (err) {
      console.error('Failed to fetch inventory types:', err);
      throw err;
    }
  };

  // 獲取 merchants（使用 list_all API 不分頁）
  const fetchMerchants = async (): Promise<MerchantSummary[]> => {
    try {
      const result = await merchantService.listAll();
      return result.data || [];
    } catch (err) {
      throw err;
    }
  };

  const fetchSalesChannels = async (): Promise<SalesChannelRecord[]> => {
    try {
      return await fetchAllPages<SalesChannelRecord>('sales_channels');
    } catch (err) {
      console.error('Failed to fetch merchant channels:', err);
      throw err;
    }
  };

  const fetchShelfTypes = async (): Promise<ShelfTypeRecord[]> => {
    try {
      const result = await shelfTypeService.listAll();
      return result.data || [];
    } catch (err) {
      console.error('Failed to fetch shelf types:', err);
      throw err;
    }
  };

  const fetchShippingTypes = async (): Promise<ShippingTypeRecord[]> => {
    try {
      const result = await shippingTypeService.listAll();
      return result.data || [];
    } catch (err) {
      console.error('Failed to fetch shipment types:', err);
      throw err;
    }
  };

  const fetchCurrencies = async (): Promise<CurrencyRecord[]> => {
    try {
      const response = await operatorApiFetch('currencies');
      const result = await response.json();
      return (result.success && result.data) ? result.data : [];
    } catch (err) {
      console.error('Failed to fetch currencies:', err);
      throw err;
    }
  };

  // 取得 Organization 體積單位（cbm / cuft）；失敗回 null，由消費端 fallback
  const fetchVolumeUnit = async (): Promise<string | null> => {
    try {
      const response = await operatorApiFetch('organizations/settings');
      const result = await response.json();
      return (result.success && result.data) ? (result.data.volume_unit ?? null) : null;
    } catch (err) {
      console.error('Failed to fetch organization volume unit:', err);
      return null;
    }
  };

  // 獲取指定 Merchant 的 inventory types
  const fetchMerchantInventoryTypes = async (targetMerchantId: string | null): Promise<any[]> => {
    try {
      if (!targetMerchantId) return [];
      // 使用 nested route 獲取特定 merchant 的設定
      return await fetchAllPages(`merchants/${targetMerchantId}/merchant_inventory_types`);
    } catch (err) {
      console.error('Failed to fetch merchant inventory types:', err);
      return []; // Return empty array on error
    }
  };

  // 取得 merchant 已安裝的 shipping types
  const fetchMerchantShippingTypes = async (targetMerchantId: string | null): Promise<MerchantShippingTypeRow[]> => {
    try {
      if (!targetMerchantId) return [];
      const result = await merchantShippingTypesService.index(targetMerchantId);
      type RawRow = {
        shipping_type_id: string;
        shipping_type_name?: string;
        shipping_type_code?: string;
        shipping_type_source?: string;
        supports_cod?: boolean;
        supports_delivery_with_pickup?: boolean;
        price?: number | string | null;
        return_shipping_fee?: number | string | null;
        shipping_rate_card_id?: string | null;
      };
      const items: MerchantShippingTypeRow[] = ((result.data || []) as RawRow[]).map((s) => ({
        id: s.shipping_type_id,  // 重要：下游消費者需要 shipping_type_id，不是 record id
        name: s.shipping_type_name ?? '',
        code: s.shipping_type_code ?? '',
        source: s.shipping_type_source,
        supports_cod: s.supports_cod,
        supports_delivery_with_pickup: s.supports_delivery_with_pickup,
        price: s.price,
        return_shipping_fee: s.return_shipping_fee,
        shipping_rate_card_id: s.shipping_rate_card_id
      }));
      return items;
    } catch (err) {
      console.error('Failed to fetch merchant shipping types:', err);
      return [];
    }
  };

  // 取得 merchant 的 sales channels
  const fetchMerchantSalesChannels = async (targetMerchantId: string | null): Promise<SalesChannelRecord[]> => {
    try {
      if (!targetMerchantId) return [];
      const result = await salesChannelService.list({ merchant_id: targetMerchantId, page_size: 1000 });
      return result.data || [];
    } catch (err) {
      console.error('Failed to fetch merchant sales channels:', err);
      return [];
    }
  };

  // 載入指定 merchant 詳細資訊（包含 order_item_columns）
  const loadMerchantInfo = async (merchantIdArg: string): Promise<MerchantSummary | null> => {
    try {
      const targetId = merchantIdArg;
      if (!targetId) return null;

      const response = await operatorApiFetch(`merchants/${targetId}`);
      const result = await response.json();

      if (result?.success && result?.data) {
        // 更新 orderItemColumns 和 availableOrderItemColumns
        if (result.data.order_item_columns !== undefined) {
          setOrderItemColumns(result.data.order_item_columns || []);
        }
        if (result.data.available_order_item_columns) {
          setAvailableOrderItemColumns(result.data.available_order_item_columns);
        }
        return result.data;
      }
      return null;
    } catch (err) {
      console.error('Failed to load merchant info:', err);
      return null;
    }
  };

  // 載入所有資料
  const loadOperatorData = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const [inventoryTypesData, merchantsData, shelfTypesData, shippingTypesData, salesChannelsData, currenciesData, volumeUnitData] = await Promise.all([
        fetchInventoryTypes(),
        fetchMerchants(),
        fetchShelfTypes(),
        fetchShippingTypes(),
        fetchSalesChannels(),
        fetchCurrencies(),
        fetchVolumeUnit()
      ]);
      setInventoryTypes(inventoryTypesData);
      setMerchants(merchantsData);
      setShelfTypes(shelfTypesData);
      setShippingTypes(shippingTypesData);
      setSalesChannels(salesChannelsData);
      setCurrencies(currenciesData);
      setVolumeUnit(volumeUnitData);
      // setMerchantInventoryTypes([]); // No longer pre-fetched globally

      // 自動選擇邏輯（優先序：單一 merchant > localStorage 恢復）
      // 使用 internal primitive 直接設定，避免 single-merchant mode 鎖定阻擋初始化
      if (merchantsData.length === 1) {
        _setMerchantInternal(merchantsData[0]);
      } else if (merchantsData.length > 1) {
        // 嘗試從 localStorage 恢復上次選擇
        const operatorId = currentOperator?.id;
        if (operatorId) {
          const tabId = getTabId();
          const stored = getStoredMerchantSelection(operatorId, tabId);
          if (stored?.merchantId) {
            const matched = merchantsData.find((m: MerchantSummary) => m.id === stored.merchantId);
            if (matched) {
              // merchant 仍存在且有權限，恢復選擇
              _setMerchantInternal(matched);
            } else {
              // merchant 已不存在或失去權限，清除過期記錄
              clearStoredMerchantSelection(operatorId, tabId);
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to load operator data:', err);
      setError(err instanceof Error ? err.message : String(err));
      setInventoryTypes([]);
      setMerchants([]);
      setShelfTypes([]);
      setShippingTypes([]);
      setCurrencies([]);
      setMerchantInventoryTypes([]);
    } finally {
      setLoading(false);
    }
  };

  // Merchant 選擇相關方法（內部 primitive，不受 single-merchant 鎖定限制）
  // 供 loadOperatorData、未認證清理等內部流程使用
  //
  // 宣告順序：必須在下方 useEffect 之前，因為 safety effect 的 deps array 會
  // 在 render 時讀取 _setMerchantInternal identity（若宣告在後會觸發 TDZ）。
  //
  // 注意：此 callback 只讀取 currentOperator（reactive），其餘均為 React 穩定的
  // state setters 或 module-level services。刻意 **不** 讀取 merchantId / merchant
  // 以避免每次 merchant 切換都重新建立 callback 導致無限迴圈。
  const _setMerchantInternal = useCallback(async (selectedMerchant: MerchantSummary | null) => {
    if (selectedMerchant) {
      setMerchant(selectedMerchant);
      setMerchantId(selectedMerchant.id);

      // 持久化到 localStorage（per-tab）
      const operatorId = currentOperator?.id;
      if (operatorId) {
        const tabId = getTabId();
        setStoredMerchantSelection(operatorId, tabId, selectedMerchant.id);
      }

      // 平行載入詳細資訊、MerchantInventoryTypes、MerchantShippingTypes、MerchantSalesChannels
      const [merchantDetails, merchantInvTypes, merchantShipTypes, merchantSaleChs] = await Promise.all([
        loadMerchantInfo(selectedMerchant.id),
        fetchMerchantInventoryTypes(selectedMerchant.id),
        fetchMerchantShippingTypes(selectedMerchant.id),
        fetchMerchantSalesChannels(selectedMerchant.id),
      ]);

      // 合併詳細資訊到 merchant 對象
      if (merchantDetails) {
        setMerchant({ ...selectedMerchant, ...merchantDetails });
      }

      // 更新 merchant-scoped data
      setMerchantInventoryTypes(merchantInvTypes);
      setMerchantShippingTypes(merchantShipTypes);
      setMerchantSalesChannels(merchantSaleChs);

    } else {
      setMerchant(null);
      setMerchantId(null);
      // 清空欄位設定
      setOrderItemColumns([]);
      setAvailableOrderItemColumns([]);
      setMerchantInventoryTypes([]);
      setMerchantShippingTypes([]);
      setMerchantSalesChannels([]);
    }
  }, [currentOperator]);

  const _resetMerchantInternal = useCallback(() => {
    // 清除 localStorage 持久化
    const operatorId = currentOperator?.id;
    if (operatorId) {
      const tabId = getTabId();
      clearStoredMerchantSelection(operatorId, tabId);
    }

    setMerchant(null);
    setMerchantId(null);
    setMerchantShippingTypes([]);
    setMerchantSalesChannels([]);
  }, [currentOperator]);

  // 只有在已認證時才載入資料（等待 AuthContext 初始化完成）
  useEffect(() => {
    // 等待認證狀態確認完成
    if (authLoading) {
      return;
    }

    // 只有在已認證時才載入資料
    if (isAuthenticated) {
      loadOperatorData();
    } else {
      // 未認證時清空資料（使用 internal primitive 避免 single-merchant 鎖定）
      setInventoryTypes([]);
      setMerchants([]);
      _setMerchantInternal(null);
      setSalesChannels([]);
      setShelfTypes([]);
      setShippingTypes([]);
      setCurrencies([]);
      setMerchantInventoryTypes([]);
      setMerchantShippingTypes([]);
      setMerchantSalesChannels([]);
      setLoading(false);
      setError(null);
    }
  }, [isAuthenticated, authLoading]);

  // Single-merchant mode safety: 當 merchants 已載入且只有一個時，
  // 確保 currentMerchant 永遠鎖定在該唯一 merchant
  //
  // 此 effect 包含 _setMerchantInternal 在 deps 中。無限迴圈防護：
  //   - guard `merchantId !== sole.id` 會在首次設定後阻擋後續觸發
  //   - _setMerchantInternal 的閉包只依賴 currentOperator，不依賴 merchantId
  useEffect(() => {
    if (!isStandard) return;
    if (!Array.isArray(merchants) || merchants.length !== 1) return;
    const sole = merchants[0];
    if (sole && merchantId !== sole.id) {
      _setMerchantInternal(sole);
    }
  }, [isStandard, merchants, merchantId, _setMerchantInternal]);


  // 重新載入貨主庫存類型
  const refreshMerchantInventoryTypes = async (): Promise<any[]> => {
    if (merchant?.id) {
      const result = await fetchMerchantInventoryTypes(merchant.id);
      setMerchantInventoryTypes(result);
      return result;
    }
    return [];
  };

  // 重新載入資料的方法
  const refreshOperatorData = (): Promise<void> => {
    return loadOperatorData();
  };

  // 重新載入物流方式（不觸發全域 loading，避免表單卸載）
  const refreshMerchantShippingTypes = async (): Promise<MerchantShippingTypeRow[]> => {
    if (!merchantId) return [];
    const data = await fetchMerchantShippingTypes(merchantId);
    setMerchantShippingTypes(data);
    return data;
  };

  // 重新載入銷售通路（不觸發全域 loading，避免表單卸載）
  const refreshMerchantSalesChannels = async (): Promise<SalesChannelRecord[]> => {
    if (!merchantId) return [];
    const data = await fetchMerchantSalesChannels(merchantId);
    setMerchantSalesChannels(data);
    return data;
  };

  // Single-merchant mode 鎖定：外部消費者呼叫的 setCurrentMerchant / resetMerchant
  // 在 single-merchant 模式下靜默成為 no-op，避免 UI 切換唯一 merchant
  const setCurrentMerchant = useCallback(async (selectedMerchant: MerchantSummary | null) => {
    if (isStandard) {
      console.warn('[OperatorData] setCurrentMerchant is a no-op in single-merchant mode');
      return;
    }
    return _setMerchantInternal(selectedMerchant);
  }, [isStandard, _setMerchantInternal]);

  const resetMerchant = useCallback(() => {
    if (isStandard) {
      console.warn('[OperatorData] resetMerchant is a no-op in single-merchant mode');
      return;
    }
    _resetMerchantInternal();
  }, [isStandard, _resetMerchantInternal]);

  const updateOrderItemColumns = (columns: string[]) => {
    setOrderItemColumns(columns);
  };

  const value: OperatorDataContextValue = {
    inventoryTypes,
    merchants,
    merchantId,
    merchant,
    currentMerchant: merchant, // Alias for compatibility with hooks
    loading,
    error,
    refreshOperatorData,
    setCurrentMerchant,
    resetMerchant,
    salesChannels,
    setSalesChannels,
    shelfTypes,
    setShelfTypes,
    shippingTypes,
    setShippingTypes,
    currencies,
    volumeUnit,
    merchantInventoryTypes,
    setMerchantInventoryTypes,
    refreshMerchantInventoryTypes,
    merchantShippingTypes,
    setMerchantShippingTypes,
    refreshMerchantShippingTypes,
    merchantSalesChannels,
    setMerchantSalesChannels,
    refreshMerchantSalesChannels,
    orderItemColumns,
    availableOrderItemColumns,
    updateOrderItemColumns,
  };

  return (
    <OperatorDataContext.Provider value={value}>
      {children}
    </OperatorDataContext.Provider>
  );
};

// 為了保留 .jsx 時期 callers 直接 destructure 不做 null-check 的行為，
// 在 admin context 等 provider 外的呼叫時回傳空 object（callers 用 `?.`/`||` fallback 即可）。
// 既有以 `useOperatorData() as ... | null` cast 的 callers 也仍可正常運作。
export const useOperatorData = (): OperatorDataContextValue => {
  const context = useContext(OperatorDataContext);
  return context ?? ({} as OperatorDataContextValue);
};

export default OperatorDataContext;
